import {
    Alert,
    Box,
    Button,
    Divider,
    Link,
    Tooltip,
    Typography,
} from '@mui/material';
import withObservables from '@nozbe/with-observables';
import { isEqual } from 'lodash';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { of } from 'rxjs';
import { InvoicesAnalytics } from 'shared/analytics/invoices';
import connectToQuickbooksIcon from 'shared/assets/images/invoicing/connectToQuickbooks.png';
import quickbooksLogo from 'shared/assets/images/invoicing/quickbooks.png';
import xeroLogo from 'shared/assets/images/invoicing/xero.png';
import DatabaseService from 'shared/db/services/Database.web';
import Invoice from 'shared/db/services/Invoice';
import OrganisationAccountings from 'shared/db/services/OrganisationAccountings';
import User from 'shared/db/services/User';
import {
    AccountingProviderData,
    AccountingProviderStatus,
    AccountingProviderWeb,
    SalesCode,
} from 'shared/types/Invoicing';
import { isAccountingProviderType } from 'shared/utils/typeGuards';

import { XeroConnectButtonIcon, XeroDisconnectButtonIcon } from '@/assets/svg';
import { AlertDialog, BackLinkButton } from '@/components';
import { WEB_STORAGE_KEYS } from '@/constants/webStorageKeys';
import { useDBSyncContext } from '@/context/DBSyncContext';
import { useImagesContext } from '@/context/ImagesContext';
import { useUserContext } from '@/context/UserContext';
import { withSyncContext } from '@/hoc';
import { useDatabase, useSyncDB } from '@/hooks';
import { ROUTE } from '@/router/routes';
import { databaseValidation } from '@/services/databaseValidation';
import { FirebaseAnalytics } from '@/services/firebase/analytics';
import { FirebaseImagesService } from '@/services/firebase/images';
import Logger from '@/services/logger';
import { dbServiceLogger } from '@/services/logger/logger';
import { AccountingProvidersAPI } from '@/services/networking/accountingProviders';
import { Snackbar } from '@/services/toastNotifications';
import { storeItemInLocalStorage } from '@/services/webStorage/localStorage';
import { COLOR } from '@/theme/colors';
import { AccountingProviderPayload } from '@/types/invoicing';

import {
    AccountingProvidersList,
    AccountingProviderSalesCodeSelect,
} from './components';
import AccountingProviderLogSection from './components/AccountingProviderLogSection/AccountingProviderLogSection';
import {
    Props,
    DialogModalsState,
    DialogType,
    OrganisationAccountingsData,
} from './types';

const formatOrganisationAccountingsData = (
    organisationAccountingsData: OrganisationAccountingsData,
) => {
    return {
        accountingDisconnectMessage:
            organisationAccountingsData.accountingDisconnectMessage || null,
        accountingProvider:
            organisationAccountingsData.accountingProvider || null,
        lockAccountingProvider: Boolean(
            organisationAccountingsData.lockAccountingProvider,
        ),
        salesAccountCode: organisationAccountingsData.salesAccountCode || null,
        id: organisationAccountingsData.id,
    };
};

function AccountingProvidersPage({
    invoicesUnsyncedWithProviderCount,
    organisationAccountings,
}: Props) {
    const { getDatabase } = useDatabase();
    const { ImagesService } = useImagesContext();
    const { userProfileData } = useUserContext();

    const { isSyncInProgress, isInitialSyncCompleted } = useDBSyncContext();
    const { syncDB } = useSyncDB();
    const { t } = useTranslation();

    const [organisationAccountingsData] = organisationAccountings || [];
    const {
        accountingProvider,
        accountingDisconnectMessage = 'Error ocurred',
        lockAccountingProvider,
        salesAccountCode,
    } = organisationAccountingsData || {};

    const [dialogModalsState, setDialogModalsState] =
        useState<DialogModalsState>({
            disconnect_provider: { isOpen: false },
            import_contacts: { isOpen: false },
            sync_invoices: { isOpen: false },
        });
    const [isDataFetching, setIsDataFetching] = useState(true);
    const [isDataFetchingError, setIsDataFetchingError] = useState(false);
    const [isContactsImportRequestPending, setIsContactsImportRequestPending] =
        useState(false);
    const [isInvoicesSyncRequestPending, setIsInvoicesSyncRequestPending] =
        useState(false);
    const [salesCodes, setSalesCodes] = useState<SalesCode[]>([]);
    const [selectedSalesCode, setSelectedSalesCode] = useState('');
    const [orgName, setOrgName] = useState<string | null>(null);

    const database = useMemo(() => {
        return getDatabase();
    }, [getDatabase]);

    const organisationAccountingsService = useMemo(() => {
        return new OrganisationAccountings({
            database,
            logDBAction: Logger.logRecordActivity,
            validation: databaseValidation,
        });
    }, [database]);

    const userService = useMemo(
        () =>
            new User({
                database,
                imageService: ImagesService,
                logDBAction: Logger.logRecordActivity,
                validation: databaseValidation,
            }),
        [ImagesService, database],
    );

    const openDialogModal = useCallback(
        (type: DialogType) => () => {
            setDialogModalsState((prev) => ({
                ...prev,
                [type]: { isOpen: true },
            }));
        },
        [],
    );

    const closeDialogModal = useCallback(
        (type: DialogType) => () => {
            setDialogModalsState((prev) => ({
                ...prev,
                [type]: { isOpen: false },
            }));
        },
        [],
    );

    const connectToXero = useCallback(async () => {
        try {
            setIsDataFetching(true);

            const data = await AccountingProvidersAPI.authorize('xero');

            window.location.href = data.payload.oauth_url;

            storeItemInLocalStorage(
                WEB_STORAGE_KEYS.user_tried_to_connect_to_xero,
                true,
            );
        } catch {
            Snackbar.showToastNotification({
                message: t('App:Messages:something_went_wrong'),
                options: {
                    variant: 'error',
                },
            });

            setIsDataFetching(false);
        }
    }, [t]);

    const connectToQuickbooks = useCallback(async () => {
        try {
            setIsDataFetching(true);

            const data = await AccountingProvidersAPI.authorize('quick_books');

            window.location.href = data.payload.oauth_url;

            storeItemInLocalStorage(
                WEB_STORAGE_KEYS.user_tried_to_connect_to_xero,
                true,
            );
        } catch {
            Snackbar.showToastNotification({
                message: t('App:Messages:something_went_wrong'),
                options: {
                    variant: 'error',
                },
            });

            setIsDataFetching(false);
        }
    }, [t]);

    const disconnectAccountingProvider = useCallback(async () => {
        setIsDataFetching(true);

        await organisationAccountingsService.disconnectFromProvider(
            organisationAccountingsData.id,
        );

        setSalesCodes([]);
        setSelectedSalesCode('');

        setIsDataFetching(false);

        InvoicesAnalytics.logUserDisconnectedWithAccountingProvider(
            FirebaseAnalytics.logEvent,
        );

        if (accountingProvider === 'xero') {
            InvoicesAnalytics.logUserDisconnectedWithXero(
                FirebaseAnalytics.logEvent,
            );
        } else if (accountingProvider === 'quick_books') {
            InvoicesAnalytics.logUserDisconnectedWithQuickbooks(
                FirebaseAnalytics.logEvent,
            );
        }
    }, [
        organisationAccountingsData.id,
        organisationAccountingsService,
        accountingProvider,
    ]);

    const handleSalesCodeChange = useCallback(
        (code: string) => {
            if (isSyncInProgress) {
                return;
            }

            setSelectedSalesCode(code);

            organisationAccountingsService.update({
                organisationAccountingsId: organisationAccountingsData.id,
                payload: { salesAccountCode: code },
            });
        },
        [
            isSyncInProgress,
            organisationAccountingsData.id,
            organisationAccountingsService,
        ],
    );

    const handleStateForFetchedAccountingData = useCallback(
        async ({
            fetchedData,
            organisationAccountingsData,
        }: {
            fetchedData: AccountingProviderData;
            organisationAccountingsData: OrganisationAccountingsData;
        }) => {
            setOrgName(fetchedData.providerOrganisationName);

            if (
                fetchedData.accountingProvider &&
                !fetchedData.accountingDisconnectMessage &&
                isAccountingProviderType(fetchedData.accountingProvider)
            ) {
                const salesCodesData =
                    await AccountingProvidersAPI.getProviderSalesCodes(
                        fetchedData.accountingProvider,
                    );

                setSalesCodes(salesCodesData.payload);

                if (!organisationAccountingsData.salesAccountCode) {
                    const defaultSalesCode = salesCodesData.payload.find(
                        (salesCode) => salesCode.default,
                    );

                    if (defaultSalesCode) {
                        const updatePayload = {
                            data: {
                                salesAccountCode: defaultSalesCode.value,
                            },
                            id: organisationAccountingsData.id,
                        };

                        await organisationAccountingsService.update({
                            payload: updatePayload.data,
                            organisationAccountingsId: updatePayload.id,
                        });
                    }
                }
            }
        },
        [setSalesCodes, organisationAccountingsService, setOrgName],
    );

    const handleDataForProvider = useCallback(
        async ({
            newData,
            organisationAccountingsData,
        }: {
            newData: AccountingProviderPayload;
            organisationAccountingsData: OrganisationAccountingsData;
        }) => {
            const {
                account_provider,
                accounting_disconnect_message,
                lock_accounting_provider,
                provider_organisation_name,
            } = newData;

            const newProviderData: Omit<
                AccountingProviderStatus,
                'providerOrganisationName'
            > = {
                accountingDisconnectMessage: accounting_disconnect_message,
                accountingProvider: account_provider,
                lockAccountingProvider: lock_accounting_provider,
            };

            const oldProviderData = {
                accountingDisconnectMessage:
                    organisationAccountingsData.accountingDisconnectMessage,
                accountingProvider:
                    organisationAccountingsData.accountingProvider,
                lockAccountingProvider:
                    organisationAccountingsData.lockAccountingProvider,
            };

            if (!isEqual(oldProviderData, newProviderData)) {
                // Run sync instead of record update to not override accounting data in push request
                // which  would reset disconnection message
                await syncDB();
            }
            const formattedData = {
                accountingDisconnectMessage: accounting_disconnect_message,
                accountingProvider: account_provider,
                lockAccountingProvider: lock_accounting_provider,
                providerOrganisationName: provider_organisation_name,
            };

            await handleStateForFetchedAccountingData({
                fetchedData: formattedData,
                organisationAccountingsData,
            });
        },
        [handleStateForFetchedAccountingData, syncDB],
    );

    const accountingDataFetchedRef = useRef(false);

    const fetchAccountingData = useCallback(
        async (organisationAccountingsData: OrganisationAccountingsData) => {
            try {
                setIsDataFetching(true);
                setIsDataFetchingError(false);

                const isInvoicingProviderConnected = Boolean(
                    organisationAccountingsData.accountingProvider,
                );

                let newData: AccountingProviderPayload | null = null;

                if (isInvoicingProviderConnected) {
                    const refreshedProviderData =
                        await AccountingProvidersAPI.refreshConnection();
                    newData = refreshedProviderData.payload;
                } else {
                    const providerData =
                        await AccountingProvidersAPI.getAccountingUserStatus();
                    newData = providerData.payload;
                }
                if (newData) {
                    await handleDataForProvider({
                        newData,
                        organisationAccountingsData,
                    });
                }

                accountingDataFetchedRef.current = true;
            } catch {
                setIsDataFetchingError(true);
            } finally {
                setIsDataFetching(false);
            }
        },
        [handleDataForProvider],
    );

    useEffect(() => {
        if (accountingProvider && salesAccountCode && !selectedSalesCode) {
            setSelectedSalesCode(salesAccountCode);
        }
    }, [accountingProvider, salesAccountCode, selectedSalesCode]);

    const triggerContactsImport = useCallback(async () => {
        try {
            setIsContactsImportRequestPending(true);

            await AccountingProvidersAPI.triggerContactsImportFromXero();

            Snackbar.showToastNotification({
                message: t('Invoicing:import_contacts_message'),
            });
        } catch {
            Snackbar.showToastNotification({
                message: t('App:Messages:something_went_wrong'),
                options: {
                    variant: 'error',
                },
            });
        } finally {
            setIsContactsImportRequestPending(false);
        }
    }, [setIsContactsImportRequestPending, t]);

    const triggerInvoicesSync = useCallback(async () => {
        try {
            setIsInvoicesSyncRequestPending(true);

            await AccountingProvidersAPI.triggerInvoicesSyncToXero();

            Snackbar.showToastNotification({
                message: t('Invoicing:sync_success'),
            });
        } catch {
            Snackbar.showToastNotification({
                message: t('App:Messages:something_went_wrong'),
                options: {
                    variant: 'error',
                },
            });
        } finally {
            setIsInvoicesSyncRequestPending(false);
        }
    }, [setIsInvoicesSyncRequestPending, t]);

    useEffect(() => {
        const syncNotRunning = isInitialSyncCompleted && !isSyncInProgress;
        const dataNotFetched =
            !accountingDataFetchedRef.current && organisationAccountingsData;

        if (syncNotRunning && dataNotFetched) {
            fetchAccountingData(organisationAccountingsData);
        }
    }, [
        fetchAccountingData,
        isInitialSyncCompleted,
        isSyncInProgress,
        organisationAccountingsData,
    ]);

    const updateUserOnboarding = useCallback(async () => {
        if (!userProfileData?.id || isSyncInProgress || !accountingProvider) {
            return;
        }
        const user = await userService.getById(userProfileData.id).fetch();
        if (user[0]?.isInvoicingAccountSetupSet) {
            return;
        }
        await userService.update(userProfileData.id, {
            isInvoicingAccountSetupSet: true,
        });
    }, [
        userProfileData?.id,
        userService,
        accountingProvider,
        isSyncInProgress,
    ]);

    useEffect(() => {
        updateUserOnboarding();
    }, [updateUserOnboarding]);

    const providers = useMemo((): AccountingProviderWeb[] => {
        return [
            {
                connect: connectToXero,
                description: t('Invoicing:xero_description'),
                image: xeroLogo,
                isAvailable: accountingProvider === null,
                isConnected: accountingProvider === 'xero',
                connectedTo: accountingProvider === 'xero' ? orgName : null,
                showContactsSync: true,
                subtitle: t('Invoicing:xero_company'),
                title: t('Invoicing:xero_name'),
                type: 'xero',
                website: {
                    text: t('Invoicing:xero_website'),
                    url: 'https://xero.com',
                },
                connectIcon: <XeroConnectButtonIcon />,
                disconnectIcon: <XeroDisconnectButtonIcon />,
            },
            {
                connect: connectToQuickbooks,
                description: t('Invoicing:quickbooks_description'),
                image: quickbooksLogo,
                isAvailable: accountingProvider === null,
                isConnected: accountingProvider === 'quick_books',
                connectedTo:
                    accountingProvider === 'quick_books' ? orgName : null,
                subtitle: t('Invoicing:quickbooks_company'),
                title: t('Invoicing:quickbooks_name'),
                type: 'quick_books',
                website: {
                    text: t('Invoicing:quickbooks_website'),
                    url: 'https://quickbooks.intuit.com',
                },
                connectIcon: (
                    <img
                        alt={`Accounting provider logo - ${t(
                            'Invoicing:quickbooks_name',
                        )}`}
                        src={connectToQuickbooksIcon}
                        style={{ height: 43 }}
                    />
                ),
                disconnectIcon: (
                    <Button style={{ color: COLOR.cancelRed }}>{`${t(
                        'Invoicing:disconnect',
                    )} ${t('Invoicing:quickbooks_name')}`}</Button>
                ),
            },
        ];
    }, [accountingProvider, connectToXero, connectToQuickbooks, orgName, t]);

    const fetchAccountingDataOnError = useCallback(async () => {
        if (organisationAccountingsData) {
            await fetchAccountingData(
                formatOrganisationAccountingsData(organisationAccountingsData),
            );
        }
    }, [fetchAccountingData, organisationAccountingsData]);

    const showProviderActions =
        !!accountingProvider && !accountingDisconnectMessage;

    const disableSalesCodesSelect =
        !salesCodes.length ||
        isSyncInProgress ||
        isDataFetching ||
        isDataFetchingError;

    const disableProvidersButtons =
        isDataFetching ||
        isSyncInProgress ||
        isDataFetchingError ||
        lockAccountingProvider;

    const disableActionsButtons =
        isDataFetching ||
        isSyncInProgress ||
        isDataFetchingError ||
        !!accountingDisconnectMessage;

    const disableImportContactsButton =
        disableActionsButtons || isContactsImportRequestPending;

    const disableSyncInvoicesButton =
        disableActionsButtons ||
        isInvoicesSyncRequestPending ||
        lockAccountingProvider ||
        invoicesUnsyncedWithProviderCount === 0;

    const SyncInvoicesButtonWrapper =
        invoicesUnsyncedWithProviderCount === 0 ? Tooltip : Box;

    return (
        <Box sx={{ pb: 4 }}>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 4,
                }}
            >
                <BackLinkButton
                    to={ROUTE.settings}
                    text="Settings"
                    testID="AccountingProvidersPage-BackButton"
                />
            </Box>
            <Box sx={{ mb: 4 }}>
                <Typography fontSize={14} fontWeight="bold" sx={{ mb: 1 }}>
                    {t('Invoicing:title')}
                </Typography>
                <Typography fontSize={14} sx={{ mb: 2 }}>
                    {t('Invoicing:info')}
                </Typography>
                <Alert severity="info">
                    <Box sx={{ mb: 0.5 }}>{t('Invoicing:note')}</Box>
                    <Box>{t('Invoicing:disclaimer')}</Box>
                </Alert>
            </Box>
            {isDataFetchingError ? (
                <Alert severity="error" sx={{ mb: 4 }}>
                    {`${t('Invoicing:errors:data_fetching_error')} `}
                    <Link
                        color={COLOR.wineBerry}
                        component="button"
                        fontWeight={700}
                        sx={{ verticalAlign: 'unset' }}
                        onClick={fetchAccountingDataOnError}
                    >
                        {t('Generic:click_here')}
                    </Link>
                    {` ${t('Invoicing:errors:to_refetch_data')}`}
                </Alert>
            ) : null}
            {accountingDisconnectMessage ? (
                <Alert severity="error" sx={{ mb: 4 }}>
                    {accountingDisconnectMessage}
                </Alert>
            ) : null}
            <Box sx={{ mr: 6 }}>
                <AccountingProvidersList
                    disableButtons={disableProvidersButtons}
                    disconnectMessage={accountingDisconnectMessage || ''}
                    isLoading={isDataFetching}
                    onDisconnectButtonClick={openDialogModal(
                        'disconnect_provider',
                    )}
                    providers={providers}
                />
            </Box>
            {showProviderActions ? (
                <Box sx={{ flex: 1, mt: 4 }}>
                    <AccountingProviderSalesCodeSelect
                        disabled={disableSalesCodesSelect}
                        isLoading={isDataFetching}
                        onChange={handleSalesCodeChange}
                        salesCodes={salesCodes}
                        value={selectedSalesCode}
                    />
                </Box>
            ) : null}
            {showProviderActions ? (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Box sx={{ display: 'flex' }}>
                        <Button
                            disabled={disableImportContactsButton}
                            onClick={openDialogModal('import_contacts')}
                            sx={{ mr: 2 }}
                            variant="text"
                        >
                            {t('Invoicing:import_contacts')}
                        </Button>
                        <SyncInvoicesButtonWrapper
                            title={t('Invoicing:all_invoices_synced') as string}
                        >
                            <div>
                                <Button
                                    disabled={disableSyncInvoicesButton}
                                    onClick={openDialogModal('sync_invoices')}
                                    variant="text"
                                >
                                    {t('Invoicing:sync_invoices')}
                                </Button>
                            </div>
                        </SyncInvoicesButtonWrapper>
                    </Box>
                </>
            ) : null}
            <Box sx={{ my: 3 }} />
            <AccountingProviderLogSection />
            {dialogModalsState.disconnect_provider.isOpen ? (
                <AlertDialog
                    close={closeDialogModal('disconnect_provider')}
                    message={t('Invoicing:dialog:disconnect_info')}
                    onConfirmButtonClick={disconnectAccountingProvider}
                    showCancelButton
                    testID="AccountingProvidersPage-DisconnectDialog"
                    title={t('Generic:are_you_sure')}
                />
            ) : null}
            {dialogModalsState.sync_invoices.isOpen ? (
                <AlertDialog
                    close={closeDialogModal('sync_invoices')}
                    message={t('Invoicing:confirm_sync')}
                    onConfirmButtonClick={triggerInvoicesSync}
                    showCancelButton
                    title={t('Actions:confirm')}
                    testID="AccountingProvidersPage-SyncInvoicesDialog"
                />
            ) : null}
            {dialogModalsState.import_contacts.isOpen ? (
                <AlertDialog
                    close={closeDialogModal('import_contacts')}
                    message={t('Invoicing:confirm_sync')}
                    onConfirmButtonClick={triggerContactsImport}
                    showCancelButton
                    title={t('Actions:confirm')}
                    testID="AccountingProvidersPage-ImportContactsDialog"
                />
            ) : null}
        </Box>
    );
}

const enhance = withObservables(
    ['isInitialSyncInProgress'],
    ({ isInitialSyncInProgress }) => {
        const database = DatabaseService.getDatabase();

        const accountingsService = new OrganisationAccountings({
            database,
            logDBAction: Logger.logRecordActivity,
            validation: databaseValidation,
        });
        const invoicesService = new Invoice({
            database,
            imageService: new FirebaseImagesService(),
            logger: dbServiceLogger,
            validation: databaseValidation,
        });

        return {
            organisationAccountings: isInitialSyncInProgress
                ? of()
                : accountingsService.get(),
            invoicesUnsyncedWithProviderCount: isInitialSyncInProgress
                ? of()
                : invoicesService
                      .getInvoicesUnsyncedWithProvider()
                      .observeCount(),
        };
    },
);

export default withSyncContext<Props>(enhance(AccountingProvidersPage));
